import {Injectable} from '@angular/core';
import * as momentNs from 'jalali-moment';
import {Moment, unitOfTime} from 'jalali-moment';
import {WeekDays} from '../common/types/week-days.type';
import {UtilsService} from '../common/services/utils/utils.service';
import {IDay} from './day.model';
import {IDayCalendarConfig, IDayCalendarConfigInternal} from './day-calendar-config.model';
import {IMonthCalendarConfig} from '../month-calendar/month-calendar-config';
const /** @type {?} */ moment = momentNs;
export class DayCalendarService {
private readonly DAYS = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'];
  readonly DEFAULT_CONFIG: IDayCalendarConfig = {
    showNearMonthDays: true,
    showWeekNumbers: false,
    firstDayOfWeek: 'sa',
    weekDayFormat: 'dd',
    format: 'YYYY/M/D',
    monthFormat: 'MMMM YY',
    dayBtnFormat: 'D',
    allowMultiSelect: false,
    enableMonthSelector: true,
    locale: 'fa'
  };
  readonly GREGORIAN_CONFIG_EXTENTION: IDayCalendarConfig = {
    firstDayOfWeek: 'su',
    weekDayFormat: 'ddd',
    format: 'DD-MM-YYYY',
    monthFormat: 'MMM, YYYY',
    locale: 'en',
    dayBtnFormat: 'DD',
    unSelectOnClick: true
  };
/**
 * @param {?} utilsService
 */
constructor(private utilsService: UtilsService) {
  }
/**
 * @param {?} currentMonth
 * @param {?} monthArray
 * @return {?}
 */
private removeNearMonthWeeks(currentMonth: Moment, monthArray: IDay[][]): IDay[][] {
    if (monthArray[monthArray.length - 1].find((day) => day.date.isSame(currentMonth, 'month'))) {
      return monthArray;
    } else {
      return monthArray.slice(0, -1);
    }
  }
/**
 * @param {?} config
 * @return {?}
 */
getConfig(config: IDayCalendarConfig): IDayCalendarConfigInternal {
    const /** @type {?} */ _config = /** @type {?} */(( <IDayCalendarConfigInternal>{
      ...this.DEFAULT_CONFIG,
      ...((config && config.locale && config.locale !== 'fa') ? this.GREGORIAN_CONFIG_EXTENTION : {}),
      ...this.utilsService.clearUndefined(config)
    }));

    this.utilsService.convertPropsToMoment(_config, _config.format, ['min', 'max'], _config.locale);

    // moment.locale(_config.locale);

    return _config;
  }
/**
 * @param {?} firstDayOfWeek
 * @return {?}
 */
generateDaysMap(firstDayOfWeek: WeekDays) {
    const /** @type {?} */ firstDayIndex = this.DAYS.indexOf(firstDayOfWeek);
    const /** @type {?} */ daysArr = this.DAYS.slice(firstDayIndex, 7).concat(this.DAYS.slice(0, firstDayIndex));
    return daysArr.reduce((map, day, index) => {
      map[day] = index;

      return map;
    }, /** @type {?} */(( <{[key: number]: string}>{})));
  }
/**
 * @param {?} config
 * @param {?} month
 * @param {?} selected
 * @return {?}
 */
generateMonthArray(config: IDayCalendarConfigInternal, month: Moment, selected: Moment[]): IDay[][] {
    let /** @type {?} */ monthArray: IDay[][] = [];
    const /** @type {?} */ firstDayOfWeekIndex = this.DAYS.indexOf(config.firstDayOfWeek);
    const /** @type {?} */ firstDayOfBoard = month.clone().startOf('month');
    for (let /** @type {?} */ i = 0; i < 8 && (firstDayOfBoard.day() !== firstDayOfWeekIndex) ; i++) {
      firstDayOfBoard.subtract(1, 'day');
      if (i === 7) {
        throw new Error('first day of Board has set Wrong');
      }
    }

    const /** @type {?} */ current = firstDayOfBoard.clone();
    const /** @type {?} */ prevMonth = month.clone().subtract(1, 'month');
    const /** @type {?} */ nextMonth = month.clone().add(1, 'month');
    const /** @type {?} */ today = moment();

    const /** @type {?} */ daysOfCalendar: IDay[] = this.utilsService.createArray(42)
      .reduce((array: IDay[]) => {
        array.push({
          date: current.clone(),
          selected: !!selected.find(selectedDay => current.isSame(selectedDay, 'day')),
          currentMonth: current.isSame(month, 'month'),
          prevMonth: current.isSame(prevMonth, 'month'),
          nextMonth: current.isSame(nextMonth, 'month'),
          currentDay: current.isSame(today, 'day'),
          disabled: this.isDateDisabled(current, config)
        });
        current.add(1, 'day');

        if (current.format('HH') !== '00') {
          current.startOf('day');
          if (array[array.length - 1].date.format('DD') === current.format('DD')) {
            current.add(1, 'day');
          }
        }

        return array;
      }, []);

    daysOfCalendar.forEach((day, index) => {
      const /** @type {?} */ weekIndex = Math.floor(index / 7);

      if (!monthArray[weekIndex]) {
        monthArray.push([]);
      }

      monthArray[weekIndex].push(day);
    });

    if (!config.showNearMonthDays) {
      monthArray = this.removeNearMonthWeeks(month, monthArray);
    }

    return monthArray;
  }
/**
 * @param {?} firstDayOfWeek
 * @param {?=} locale
 * @return {?}
 */
generateWeekdays(firstDayOfWeek: WeekDays, locale?: string): Moment[] {
    const /** @type {?} */ weekdayNames: {[key: string]: Moment} = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'].reduce((acc, d, i) => {
      const /** @type {?} */ m = moment();
      if (locale) {
        m.locale(locale);
      }
      m.day(i);
      acc[d] = m;
      return acc;
    }, {});
    const /** @type {?} */ weekdays: Moment[] = [];
    const /** @type {?} */ daysMap = this.generateDaysMap(firstDayOfWeek);

    for (const /** @type {?} */ dayKey in daysMap) {
      if (daysMap.hasOwnProperty(dayKey)) {
        weekdays[daysMap[dayKey]] = weekdayNames[dayKey];
      }
    }

    return weekdays;
  }
/**
 * @param {?} date
 * @param {?} config
 * @return {?}
 */
isDateDisabled(date: Moment, config: IDayCalendarConfigInternal): boolean {
    if (config.isDayDisabledCallback) {
      return config.isDayDisabledCallback(date);
    }

    if (config.min && date.isBefore(config.min, 'day')) {
      return true;
    }

    return !!(config.max && date.isAfter(config.max, 'day'));
  }
/**
 * @param {?} config
 * @param {?} month
 * @return {?}
 */
getHeaderLabel(config: IDayCalendarConfigInternal, month: Moment): string {
    if (config.monthFormatter) {
      return config.monthFormatter(month);
    }
    month.locale(config.locale);
    return month.format(config.monthFormat);
  }
/**
 * @param {?} min
 * @param {?} currentMonthView
 * @return {?}
 */
shouldShowLeft(min: Moment, currentMonthView: Moment): boolean {
    return min ? min.isBefore(currentMonthView, 'month') : true;
  }
/**
 * @param {?} max
 * @param {?} currentMonthView
 * @return {?}
 */
shouldShowRight(max: Moment, currentMonthView: Moment): boolean {
    return max ? max.isAfter(currentMonthView, 'month') : true;
  }
/**
 * @param {?} firstDayOfWeek
 * @return {?}
 */
generateDaysIndexMap(firstDayOfWeek: WeekDays) {
    const /** @type {?} */ firstDayIndex = this.DAYS.indexOf(firstDayOfWeek);
    const /** @type {?} */ daysArr = this.DAYS.slice(firstDayIndex, 7).concat(this.DAYS.slice(0, firstDayIndex));
    return daysArr.reduce((map, day, index) => {
      map[index] = day;

      return map;
    }, /** @type {?} */(( <{[key: number]: string}>{})));
  }
/**
 * @param {?} componentConfig
 * @return {?}
 */
getMonthCalendarConfig(componentConfig: IDayCalendarConfigInternal): IMonthCalendarConfig {
    return this.utilsService.clearUndefined({
      min: componentConfig.min,
      max: componentConfig.max,
      format: componentConfig.format,
      isNavHeaderBtnClickable: true,
      allowMultiSelect: false,
      yearFormat: componentConfig.yearFormat,
      locale: componentConfig.locale,
      yearFormatter: componentConfig.yearFormatter,
      monthBtnFormat: componentConfig.monthBtnFormat,
      monthBtnFormatter: componentConfig.monthBtnFormatter,
      monthBtnCssClassCallback: componentConfig.monthBtnCssClassCallback,
      multipleYearsNavigateBy: componentConfig.multipleYearsNavigateBy,
      showMultipleYearsNavigation: componentConfig.showMultipleYearsNavigation,
      showGoToCurrent: componentConfig.showGoToCurrent
    });
  }
/**
 * @param {?} config
 * @param {?} day
 * @return {?}
 */
getDayBtnText(config: IDayCalendarConfigInternal, day: Moment): string {
    if (config.dayBtnFormatter) {
      return config.dayBtnFormatter(day);
    }

    return day.format(config.dayBtnFormat);
  }
/**
 * @param {?} config
 * @param {?} day
 * @return {?}
 */
getDayBtnCssClass(config: IDayCalendarConfigInternal, day: Moment): string {
    if (config.dayBtnCssClassCallback) {
      return config.dayBtnCssClassCallback(day);
    }

    return '';
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: UtilsService, },
];
}

function DayCalendarService_tsickle_Closure_declarations() {
/** @type {?} */
DayCalendarService.decorators;
/**
 * @nocollapse
 * @type {?}
 */
DayCalendarService.ctorParameters;
/** @type {?} */
DayCalendarService.prototype.DAYS;
/** @type {?} */
DayCalendarService.prototype.DEFAULT_CONFIG;
/** @type {?} */
DayCalendarService.prototype.GREGORIAN_CONFIG_EXTENTION;
/** @type {?} */
DayCalendarService.prototype.utilsService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
